import {
  BASE_INTERVAL,
  type ExponentialRangeConfig,
  type ExponentialRangeMarker,
} from "../const";
import { getExponentialValue } from "./getExponentialRangeValue";

/**
 * @todo add documentation for how this works.
 */

export function getExponentialRangeConfig(
  min: number,
  max: number,
): ExponentialRangeConfig {
  let steps = 0;
  const clampedMinimumValue = Math.max(1, min);
  const exponentialMinimumValue = getExponentialValue(clampedMinimumValue);
  const exponentialMaximumValue = getExponentialValue(max);
  const intervals = exponentialMaximumValue - exponentialMinimumValue + 1;

  const markers: ExponentialRangeMarker[] = [];

  const ranges = Array.from({ length: intervals }, (_, index) => {
    const localStepSize = BASE_INTERVAL ** (exponentialMinimumValue + index);
    const localMinValue = Math.max(
      clampedMinimumValue,
      Math.min(max, localStepSize),
    );
    const localMaxValue = Math.max(
      clampedMinimumValue,
      Math.min(max, localStepSize * (BASE_INTERVAL - 1)),
    );
    const localStepCount =
      Math.ceil((localMaxValue - localMinValue) / localStepSize) + 1;

    steps += localStepCount;

    const localMinInternal = steps - localStepCount + 1;

    markers.push({
      value: localMinInternal,
      label: localMinValue,
    });

    return {
      stepSize:
        exponentialMinimumValue + index === -1
          ? 1
          : BASE_INTERVAL ** (exponentialMinimumValue + index),
      externalMinValue: localMinValue,
      internalMinValue: localMinInternal,
      internalMaxExclusive: steps + 1,
    };
  });

  return {
    min: min === 0 ? 0 : 1,
    max: steps,
    markers,
    toInternal: (value: number) => {
      if (value < 1) {
        return 0;
      }

      const exponentialValue =
        getExponentialValue(value) - exponentialMinimumValue;

      if (exponentialValue >= intervals) {
        return steps;
      }

      const range = ranges[exponentialValue];
      if (!range) {
        return 0;
      }
      return Math.max(
        0,
        Math.min(
          steps,
          range.internalMinValue +
            (value - range.externalMinValue) / range.stepSize,
        ),
      );
    },
    toExternal: (value: number) => {
      if (value <= 0) {
        return min;
      }
      if (value >= steps) {
        return max;
      }

      for (let index = 0; index < intervals; index += 1) {
        const range = ranges[index];
        if (!range) {
          return max;
        }
        if (
          range.internalMinValue <= value &&
          value < range.internalMaxExclusive
        ) {
          return Math.max(
            min,
            Math.min(
              max,
              range.externalMinValue +
                (value - range.internalMinValue) * range.stepSize,
            ),
          );
        }
      }

      return max;
    },
  };
}
